//#region React Imports
import React, { useContext, useState, useEffect, useCallback } from 'react';

//service
import AppChargersService from '../../AppChargersService';

//Components
import ChargerProgress from './ChargerProgressBar/ChargerProgress';
import ChargingStatus from './ChargingStatus';
import ChargerSelections from './ChargerSelections';
import ChargerTimeSummary from './ChargerTimeSummary';
import ChargerButton from './ChargerButton';
import ChargerOCPPMessage from './ChargerOCPPMesage';
import { LoadingSpinner } from '../../../../shared';

//context
import { CsoAccountContext } from '../../../../context/operator/getCsoAccount';

//styles
import { DivStyles } from '../../../../SharedStyles';

//helpers
import { connectorConstants } from '../../../../driver/helpers';

const ControlCharger = (props) => {
  const { strings } = AppChargersService;
  const { charger } = props;
  const chargingSessionPollTime = 10000;
  const statusPollTime = 5000;
  const [pollETA, setPollETA] = useState(false);

  //current charging station via props - can change based on url id and redirects
  const [connectors, setConnectors] = useState([]);
  const [selectedPort, setSelectedPort] = useState(
    charger.evses[0].connectors.length === 1
      ? charger.evses[0].connectors[0].id
      : ''
  );
  const [chargerDisabled, setChargerDisabled] = useState(false);
  const [selectedRange, setSelectedRange] = useState(0);
  const [loading, setLoading] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [chargingSessionEta, setChargingSessionEta] = useState({});
  const [showSummary, setShowSummary] = useState(false);

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [startButtonText, setStartButtonText] = useState(strings.startCharging);
  const [idTag, setIdTag] = useState('');
  const [portStatus, setPortStatus] = useState(0); 

  const { currentUser } = useContext(
    CsoAccountContext
  );

  const handleChargerDiagnostics = useCallback( async (reset) => {
    setLoading(true);
    if (reset) {
      handleResetAllOnPortChange();
    }
    let res = await AppChargersService.viewChargingStationDiagnostics({
      iotHubDeviceId: charger.iotHubDeviceId,
      port: selectedPort,
    });
    setDiagnostics(res);
    if (res.authenticatedUser && res.authenticatedUser.chargeRangeInfo) {
      setSelectedRange(res.authenticatedUser.chargeRangeInfo.desiredMaxRange);
    }
    setLoading(false);
  }, [charger.iotHubDeviceId, selectedPort]);


  const handleResetAllOnPortChange = () => {
    setPortStatus(0);
    setShowSummary(false);
    setChargingSessionEta({});
    setDiagnostics(null);
    setSelectedRange('');
    setPollETA(false);
  };

  useEffect(() => {
    const fetchStationStatus = async () => {
      let res = await AppChargersService.getChargingStationStatus({
        iotHubDeviceId: charger.iotHubDeviceId, 
        addLastReadings: true,
        connectorId: selectedPort,
      });
      setConnectors(res.connectorStatuses);

      if (res.statusDescription === 'Disabled' ||
          res.statusDescription === 'Off-line') {
        setChargerDisabled(true);
      }
      else {
        setChargerDisabled(false);
      }

      if (selectedPort && res.connectorStatuses.length > 0) {
        const status = res.connectorStatuses[selectedPort - 1].status;
        setPortStatus(status);
        if (status === connectorConstants.Charging ||
            status === connectorConstants.Finishing) {
          setPollETA(true);
          if (status === connectorConstants.Charging 
            && diagnostics 
            && diagnostics.activeTransactionId === -1) {
            handleChargerDiagnostics(false);
          }
        }
        else if (status === connectorConstants.Available) {
          if (diagnostics) { // && diagnostics.authenticatedUser) {
            console.log('Available - resetting diagnostics');
            handleResetAllOnPortChange();
          }
        }
      }
      else {
        setPortStatus(0);
        setPollETA(false);
      }    
    };

    fetchStationStatus();
    const statusPoll = setInterval(() => {fetchStationStatus();}, statusPollTime);

    return () => {
      clearInterval(statusPoll);
    };
  }, [charger.iotHubDeviceId, selectedPort, diagnostics, handleChargerDiagnostics]);

  useEffect(() => {
    console.log('selectedPort changed', selectedPort);
    if (selectedPort) {
      handleChargerDiagnostics(true);
    }
    else {
        handleResetAllOnPortChange();
    }    
  }, [handleChargerDiagnostics, selectedPort]);

  
  //Handle Selection - will trigger the Diagnostics api check
  const handleSelections = useCallback( (key, value) => {
    if (key === 'port') {
      setSelectedPort(value);
    }
    if (key === 'desiredMiles') {
      setSelectedRange(value);
    }
    if (key === 'idTag') {
      setIdTag(value);
    }
    if (key === 'startRangeOfVehicle' && diagnostics && diagnostics.authenticatedUser && diagnostics.authenticatedUser.chargeRangeInfo) {
      diagnostics.authenticatedUser.chargeRangeInfo = {...diagnostics.authenticatedUser.chargeRangeInfo, startRangeOfVehicle: value};
      setDiagnostics({...diagnostics});
    }
    if (key === 'maxRangeOfVehicle' && diagnostics && diagnostics.authenticatedUser && diagnostics.authenticatedUser.chargeRangeInfo) {
      diagnostics.authenticatedUser.chargeRangeInfo = {...diagnostics.authenticatedUser.chargeRangeInfo, maxRangeOfVehicle: value};
      setDiagnostics({...diagnostics});
    }
  }, [diagnostics]);

  //Disable and Enable Start now button
  useEffect(() => {
    if (
      (selectedRange > 0 || idTag.length > 0) &&
      selectedPort &&
      // Object.keys(chargingSessionEta).length === 0 &&
      (portStatus === connectorConstants.Charging 
        || portStatus === connectorConstants.Preparing)
    ) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
    if (showSummary || portStatus === connectorConstants.Finishing) {
       setStartButtonText('Finished');
    }
    else if (idTag.length === 0) {
      setStartButtonText(portStatus === connectorConstants.Charging 
        ? strings.stopCharging : strings.startCharging);
    }
    else {
      setStartButtonText(portStatus === connectorConstants.Charging 
        ? strings.stopCharging : strings.startAsDriverCharging);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPort, selectedRange, portStatus, idTag]);

  const handleStartStop = async () => {
    try {
      setIsButtonDisabled(true);
      setLoading(true);

      const csoTestSession = idTag.length === 0;
      const isCharging = portStatus === connectorConstants.Charging;

      if (!isCharging) {
        await AppChargersService.startChargingSession({
          iotHubDeviceId: charger.iotHubDeviceId,
          userId: diagnostics?.authenticatedUser?.userId,
          vehicleId: '',
          idToken: idTag,
          connectorId: selectedPort,
          couponId: null,
          csoTestSession: csoTestSession,
          allowAutoPayment: false, //will change to state if enable funds returns
          chargeRangeInfo: {
            desiredMaxRange: selectedRange,
            startRangeOfVehicle: 0,
            maxRangeOfVehicle: 500,
            vehicleBatteryCapacity: 120,
          },
        });
        // if (!diagnostics.authenticatedUser) {
          handleChargerDiagnostics(false);
        // }
        setPollETA(true);
      } else {
        await AppChargersService.stopChargingSession({
          iotHubDeviceId: charger.iotHubDeviceId,
          vehicleId: diagnostics.vehicle,
          userId: diagnostics.authenticatedUser
            ? diagnostics.authenticatedUser.userId
            : null,
        });
        setPollETA(false);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log('handleStartStop.catch: status', 0);
      setPortStatus(0);
      setIsButtonDisabled(false);
      console.log(err);
    }
  };

  /**
   * Keeps the ETA information up-to-date.
   * Called periodically by setInterval during a charging session.
   */
  const updateSessionEta = useCallback( async (authenticatedUser) => {
    // try {
      let maxRange = selectedRange > 0 ? selectedRange : 500;
      let startRange = 0;
      let desiredRange = selectedRange;
      let batteryCap = 120;

      if (authenticatedUser?.chargeRangeInfo) {
        maxRange = authenticatedUser.chargeRangeInfo.maxRangeOfVehicle;
        startRange = authenticatedUser.chargeRangeInfo.startRangeOfVehicle;
        desiredRange = authenticatedUser.chargeRangeInfo.desiredMaxRange;
        batteryCap = authenticatedUser.chargeRangeInfo.vehicleBatteryCapacity;
      }

      try {
        let res = await AppChargersService.getChargingSessionEta({
          iotHubDeviceId: charger.iotHubDeviceId,
          desiredMaxRange: desiredRange,
          startRangeOfVehicle: startRange,
          maxRangeOfVehicle: maxRange,
          vehicleBatteryCapacity: batteryCap,
          csoTestSession: idTag.length === 0,
          userId: authenticatedUser
            ? authenticatedUser.userId
            : null,
          connectorId: authenticatedUser
            ? selectedPort
            : 0,
        });
        setChargingSessionEta(res);

        //do it in fetchStationStatus ???
        if (portStatus === connectorConstants.Finishing) {
          setShowSummary(true);
        }
        if (res.isChargingComplete ||
            res.activeTransactionId === -1 ||
            res.authenticatedUser === null
        ) {
          setPollETA(false);
        }
      } catch (err) {
        console.log('Oh no', err);
      }
  }, [charger.iotHubDeviceId, idTag.length, portStatus, selectedPort, selectedRange]);

  // /**
  //  * Toggle ETA polling on and off depending on the
  //  * existence of a charging session for the current
  //  * user.
  //  */
  useEffect(() => {
    if (chargingSessionEta.isChargingComplete) {
      setPollETA(false);
      // setIsFinished(true);
      // setDiagnostics(true);
    }
  }, [chargingSessionEta.isChargingComplete]);

  useEffect(() => {
    let pollingTimer;
    if (pollETA) {
      console.log('triggered:', diagnostics?.authenticatedUser);
      pollingTimer = setInterval(() => {updateSessionEta(diagnostics?.authenticatedUser);}, chargingSessionPollTime);
    } else if (pollingTimer){
      console.log('stopped');
      clearInterval(pollingTimer);
    }
    return () => {
      clearInterval(pollingTimer);
    };
  }, [diagnostics?.authenticatedUser, pollETA, updateSessionEta]);

  return (
    <DivStyles>
      {loading && <LoadingSpinner />}
      <ChargerSelections
        charger={charger}
        diagnostics={diagnostics}
        selectedPort={selectedPort}
        selectedRange={selectedRange}
        portStatus={portStatus}
        disableMiles={portStatus === connectorConstants.Charging || portStatus === connectorConstants.Finishing}
        isAdminUser={currentUser && (currentUser.isAdminUser || currentUser.role === 1)}
        handleSelections={handleSelections}
      />
      <ChargingStatus
        strings={strings}
        charger={charger}
        connectors={connectors}
        selectedPort={selectedPort}
      />
      <ChargerProgress
        strings={strings}
        charger={charger}
        chargingSessionEta={chargingSessionEta}
        diagnostics={diagnostics}
        isCharging={portStatus === connectorConstants.Charging}
        selectedRange={selectedRange}
        handleStartStop={handleStartStop}
      />
      <ChargerTimeSummary
        strings={strings}
        charger={charger}
        chargingSessionEta={chargingSessionEta}
        showSummary={showSummary}
        diagnostics={diagnostics}
      />
      {!chargerDisabled && <ChargerButton
        buttonText={startButtonText}
        disabled={isButtonDisabled}
        handleStartStop={handleStartStop}
        handleSelections={handleSelections}
        isAdminUser={currentUser && (currentUser.isAdminUser || currentUser.role === 1)}
        idTag={idTag}
      />}
      {currentUser && (currentUser.isAdminUser || currentUser.role === 1) &&
      <ChargerOCPPMessage
        charger={charger}
      />}
    </DivStyles>
  );
};

export default ControlCharger;
